import { Button } from '@components/button';
import React, {
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useEffect,
    useRef,
} from 'react';

export function OverlaySection({
    title,
    description,
}: {
    title?: string;
    description: string | ReactNode;
}): ReactElement {
    return description ? (
        <div key={title}>
            {title ? (
                <div className="font-semibold font-sans">{title}</div>
            ) : (
                <></>
            )}
            <div>{description}</div>
        </div>
    ) : (
        <></>
    );
}

const useFocusManagement = (
    visible: boolean,
    overlayRef: React.RefObject<HTMLDivElement>,
) => {
    const previousFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (visible) {
            previousFocusRef.current = document.activeElement as HTMLElement;
            if (overlayRef.current) {
                overlayRef.current.focus();
            }
        } else if (previousFocusRef.current) {
            previousFocusRef.current.focus();
        }
    }, [visible, overlayRef]);
};

const useOutsideClickAndEscape = (
    visible: boolean,
    overlayRef: React.RefObject<HTMLDivElement>,
    setVisible: (newVisible: boolean) => void,
) => {
    useEffect(() => {
        if (!visible) {
            return;
        }

        let mouseDownTarget: EventTarget | null = null;

        const handleMouseDown = (event: MouseEvent) => {
            mouseDownTarget = event.target;
        };

        const handleMouseUp = (event: MouseEvent) => {
            const isOutsideClick =
                overlayRef.current &&
                !overlayRef.current.contains(event.target as Node) &&
                mouseDownTarget &&
                !overlayRef.current.contains(mouseDownTarget as Node);

            if (isOutsideClick) {
                setVisible(false);
            }
            mouseDownTarget = null;
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setVisible(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [visible, overlayRef, setVisible]);
};

export function Overlay({
    children,
    setVisible,
    visible = false,
}: PropsWithChildren<{
    visible: boolean;
    setVisible: (newVisible: boolean) => void;
}>): ReactElement {
    const overlayRef = useRef<HTMLDivElement>(null);

    useFocusManagement(visible, overlayRef);
    useOutsideClickAndEscape(visible, overlayRef, setVisible);

    return visible ? (
        <div className="absolute xs-only:left-1/2 xs-only:-translate-y-1/2 xs-only:-translate-x-1/2 xs-only:transform sm:right-[26px] z-10 w-screen max-w-xs px-2 sm:px-0">
            <div
                ref={overlayRef}
                className="overflow-hidden rounded-sm shadow-md ring-1 ring-spacer ring-opacity-20 focus:outline-none focus:ring-2 focus:ring-red"
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
            >
                <div className="relative grid gap-2 bg-white p-2 sm:p-4">
                    {children}
                    <div className="flex justify-center mt-4">
                        <Button
                            title="Overlay schließen"
                            onClick={() => setVisible(false)}
                            className="bg-transparent"
                        />
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <></>
    );
}
